import { format } from 'date-fns';
import types from '../mutation-types';
import AiSummariesAPI from 'dashboard/api/aiSummaries';

const defaultFilters = () => ({
  inboxId: '',
  status: 'all',
  datePreset: 'last_7_days',
  customRange: [],
  perPage: 20,
});

const defaultPagination = () => ({
  currentPage: 1,
  perPage: 20,
  totalPages: 0,
  totalCount: 0,
});

const state = {
  filters: defaultFilters(),
  conversations: [],
  pagination: defaultPagination(),
  uiFlags: {
    isLoading: false,
    isRegenerating: false,
    regeneratingId: null,
  },
  filtersLoaded: false,
};

const getters = {
  getAiSummaryConversations: $state => $state.conversations,
  getAiSummaryPagination: $state => $state.pagination,
  getAiSummaryFilters: $state => $state.filters,
  getAiSummaryUIFlags: $state => $state.uiFlags,
  getAiSummaryFiltersLoaded: $state => $state.filtersLoaded,
};

const dateRangeForPreset = preset => {
  const today = new Date();
  const formatDate = date => format(date, 'yyyy-MM-dd');

  switch (preset) {
    case 'last_30_days':
      return {
        from: formatDate(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)),
        to: formatDate(today),
      };
    case 'custom':
      return null;
    case 'last_7_days':
    default:
      return {
        from: formatDate(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)),
        to: formatDate(today),
      };
  }
};

const buildParams = filters => {
  const params = {
    status: filters.status || 'all',
    per_page: filters.perPage || 20,
  };

  if (filters.inboxId) {
    params.inbox_id = filters.inboxId;
  }

  const presetRange = dateRangeForPreset(filters.datePreset);

  if (filters.datePreset === 'custom' && filters.customRange?.length === 2) {
    params.date_from = format(filters.customRange[0], 'yyyy-MM-dd');
    params.date_to = format(filters.customRange[1], 'yyyy-MM-dd');
  } else if (presetRange) {
    params.date_from = presetRange.from;
    params.date_to = presetRange.to;
  }

  return params;
};

const persistFilters = filters => {
  try {
    localStorage.setItem('aiSummaryFilters', JSON.stringify(filters));
  } catch (_) {
    // ignore
  }
};

const loadFiltersFromStorage = () => {
  try {
    const saved = localStorage.getItem('aiSummaryFilters');
    if (!saved) {
      return defaultFilters();
    }
    const parsed = JSON.parse(saved);
    if (parsed.customRange?.length === 2) {
      parsed.customRange = parsed.customRange.map(value =>
        value ? new Date(value) : value
      );
    }
    return { ...defaultFilters(), ...parsed };
  } catch (_) {
    return defaultFilters();
  }
};

export const actions = {
  initialize({ commit }) {
    const filters = loadFiltersFromStorage();
    commit(types.SET_AI_SUMMARY_FILTERS, filters);
    commit(types.SET_AI_SUMMARY_FILTERS_LOADED, true);
  },

  updateAiSummaryFilters({ commit }, partial) {
    commit(types.SET_AI_SUMMARY_FILTERS, partial);
  },

  async generateAiSummaries({ commit, state, rootGetters }, overrides = {}) {
    commit(types.SET_AI_SUMMARY_UI_FLAGS, { isLoading: true, regeneratingId: null });
    const accountId = rootGetters.getCurrentAccountId;

    try {
      const params = {
        ...buildParams(state.filters),
        page: overrides.page || state.pagination.currentPage || 1,
      };
      if (overrides.forceRegenerate) {
        params.force_regenerate = true;
      }

      const response = await AiSummariesAPI.generateBulk(accountId, params);
      const data = response.data || {};
      commit(types.SET_AI_SUMMARY_RESULTS, data.conversations || []);
      commit(types.SET_AI_SUMMARY_PAGINATION, {
        currentPage: data.pagination?.current_page || params.page,
        perPage: params.per_page,
        totalCount: data.pagination?.total_count || 0,
        totalPages: data.pagination?.total_pages || 0,
      });
      persistFilters(state.filters);
    } catch (error) {
      console.error('generateAiSummaries failed', error);
      throw error;
    } finally {
      commit(types.SET_AI_SUMMARY_UI_FLAGS, { isLoading: false });
    }
  },

  async changeAiSummaryPage({ dispatch, commit }, page) {
    commit(types.SET_AI_SUMMARY_PAGINATION, { currentPage: page });
    await dispatch('generateAiSummaries', { page });
  },

  async regenerateAiSummary({ commit, rootGetters }, conversation) {
    const accountId = rootGetters.getCurrentAccountId;
    commit(types.SET_AI_SUMMARY_UI_FLAGS, { regeneratingId: conversation.id });
    try {
      const response = await AiSummariesAPI.regenerate(
        accountId,
        conversation.display_id || conversation.id,
        { conversation_id: conversation.display_id || conversation.id }
      );

      commit(types.UPDATE_AI_SUMMARY_ROW, {
        conversationId: conversation.id,
        summary: response.data.summary,
      });
      return response.data;
    } catch (error) {
      console.error('regenerate summary failed', error);
      throw error;
    } finally {
      commit(types.SET_AI_SUMMARY_UI_FLAGS, { regeneratingId: null });
    }
  },
};

export const mutations = {
  [types.SET_AI_SUMMARY_FILTERS]($state, data) {
    $state.filters = { ...$state.filters, ...data };
    $state.pagination = {
      ...$state.pagination,
      perPage: $state.filters.perPage,
    };
  },
  [types.SET_AI_SUMMARY_FILTERS_LOADED]($state, value) {
    $state.filtersLoaded = value;
  },
  [types.SET_AI_SUMMARY_RESULTS]($state, rows) {
    $state.conversations = rows;
  },
  [types.SET_AI_SUMMARY_PAGINATION]($state, payload) {
    $state.pagination = {
      ...$state.pagination,
      ...payload,
    };
  },
  [types.SET_AI_SUMMARY_UI_FLAGS]($state, flags) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...flags,
    };
  },
  [types.UPDATE_AI_SUMMARY_ROW]($state, payload) {
    $state.conversations = $state.conversations.map(row =>
      row.id === payload.conversationId ? { ...row, summary: payload.summary } : row
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
